<template>
  <div class="autocomplete" ref="rootRef">
    <label v-if="label" :for="inputId" class="autocomplete__label">{{ label }}</label>

    <div
      class="autocomplete__wrapper"
      role="combobox"
      :aria-expanded="isOpen"
      aria-haspopup="listbox"
      :aria-owns="listboxId"
    >
      <input
        :id="inputId"
        ref="inputRef"
        v-model="inputText"
        type="text"
        class="autocomplete__input"
        :class="{ 'autocomplete__input--loading': isLoading, 'autocomplete__input--error': hasError }"
        :placeholder="placeholder"
        :disabled="disabled"
        autocomplete="off"
        :aria-autocomplete="'list'"
        :aria-controls="listboxId"
        :aria-activedescendant="activeDescendant"
        @input="onInput"
        @keydown="onKeydown"
        @focus="onFocus"
        @blur="onBlur"
      />

      <span v-if="isLoading" class="autocomplete__spinner" aria-hidden="true">
        <svg class="autocomplete__spinner-svg" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <circle class="autocomplete__spinner-circle" cx="12" cy="12" r="10" fill="none" stroke-width="3" />
        </svg>
      </span>

      <button
        v-if="inputText && !disabled"
        type="button"
        class="autocomplete__clear"
        aria-label="Clear"
        tabindex="-1"
        @mousedown.prevent="clearInput"
      >
        &times;
      </button>
    </div>

    <p v-if="errorMessage" class="autocomplete__error" role="alert">{{ errorMessage }}</p>

    <ul
      v-if="isOpen && results.length > 0"
      :id="listboxId"
      role="listbox"
      class="autocomplete__listbox"
    >
      <li
        v-for="(item, index) in results"
        :key="item.id"
        :id="optionId(index)"
        role="option"
        class="autocomplete__option"
        :class="{ 'autocomplete__option--active': index === activeIndex }"
        :aria-selected="index === activeIndex"
        @mousedown.prevent="selectItem(item)"
        @mouseover="activeIndex = index"
      >
        {{ item.name }}
      </li>
    </ul>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue'
import axiosInstance from '@/axios-instance'

const props = defineProps({
  modelValue: {
    type: Object,
    default: null,
  },
  searchUrl: {
    type: String,
    required: true,
  },
  placeholder: {
    type: String,
    default: '',
  },
  label: {
    type: String,
    default: '',
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  minChars: {
    type: Number,
    default: 2,
  },
  debounceMs: {
    type: Number,
    default: 300,
  },
})

const emit = defineEmits(['update:modelValue'])

// Unique IDs for accessibility
const uid = Math.random().toString(36).slice(2, 9)
const inputId = `autocomplete-input-${uid}`
const listboxId = `autocomplete-listbox-${uid}`
const optionId = (index) => `autocomplete-option-${uid}-${index}`

const rootRef = ref(null)
const inputRef = ref(null)
const inputText = ref('')
const results = ref([])
const isOpen = ref(false)
const isLoading = ref(false)
const errorMessage = ref('')
const activeIndex = ref(-1)
let debounceTimer = null

const hasError = computed(() => !!errorMessage.value)
const activeDescendant = computed(() =>
  activeIndex.value >= 0 ? optionId(activeIndex.value) : undefined
)

// Pre-fill input from modelValue on mount and when it changes externally
watch(
  () => props.modelValue,
  (val) => {
    if (val && val.name !== undefined) {
      inputText.value = val.name
    } else if (!val) {
      inputText.value = ''
    }
  },
  { immediate: true }
)

function onInput() {
  errorMessage.value = ''

  if (!inputText.value) {
    clearResults()
    emit('update:modelValue', null)
    return
  }

  if (inputText.value.length < props.minChars) {
    clearResults()
    return
  }

  clearTimeout(debounceTimer)
  debounceTimer = setTimeout(() => {
    fetchResults(inputText.value)
  }, props.debounceMs)
}

async function fetchResults(term) {
  isLoading.value = true
  errorMessage.value = ''
  try {
    const response = await axiosInstance.get(props.searchUrl, {
      params: { q: term },
    })
    const data = response.data
    results.value = Array.isArray(data) ? data : (data.results || [])
    isOpen.value = results.value.length > 0
    activeIndex.value = -1
  } catch {
    errorMessage.value = 'Search failed. Please try again.'
    clearResults()
  } finally {
    isLoading.value = false
  }
}

function selectItem(item) {
  inputText.value = item.name
  emit('update:modelValue', { id: item.id, name: item.name })
  clearResults()
}

function clearInput() {
  inputText.value = ''
  emit('update:modelValue', null)
  clearResults()
  inputRef.value?.focus()
}

function clearResults() {
  results.value = []
  isOpen.value = false
  activeIndex.value = -1
}

function onKeydown(event) {
  if (!isOpen.value) return

  switch (event.key) {
    case 'ArrowDown':
      event.preventDefault()
      activeIndex.value = Math.min(activeIndex.value + 1, results.value.length - 1)
      break
    case 'ArrowUp':
      event.preventDefault()
      activeIndex.value = Math.max(activeIndex.value - 1, 0)
      break
    case 'Enter':
      event.preventDefault()
      if (activeIndex.value >= 0 && results.value[activeIndex.value]) {
        selectItem(results.value[activeIndex.value])
      }
      break
    case 'Escape':
      event.preventDefault()
      clearResults()
      break
  }
}

function onFocus() {
  if (results.value.length > 0) {
    isOpen.value = true
  }
}

function onBlur() {
  // Delay to allow mousedown on option to fire first
  setTimeout(() => {
    clearResults()
  }, 150)
}

function handleClickOutside(event) {
  if (rootRef.value && !rootRef.value.contains(event.target)) {
    clearResults()
  }
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
})

onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutside)
  clearTimeout(debounceTimer)
})
</script>

<style scoped>
.autocomplete {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.autocomplete__label {
  font-family: 'Roboto', sans-serif;
  font-size: 0.9rem;
  font-weight: 500;
  color: var(--text-black);
}

.autocomplete__wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.autocomplete__input {
  width: 100%;
  padding: 0.5rem 2.5rem 0.5rem 0.75rem;
  font-family: 'Roboto', sans-serif;
  font-size: 1rem;
  color: var(--text-black);
  background: #fff;
  border: 1px solid var(--gray-200);
  border-radius: 6px;
  outline: none;
  transition: border-color 0.15s;
  box-sizing: border-box;
}

.autocomplete__input:focus {
  border-color: var(--halsa-green);
  box-shadow: 0 0 0 2px rgba(0, 209, 181, 0.15);
}

.autocomplete__input:disabled {
  background: var(--gray-100);
  color: var(--text-grey);
  cursor: not-allowed;
}

.autocomplete__input--error {
  border-color: var(--halsa-red);
}

.autocomplete__spinner {
  position: absolute;
  right: 0.6rem;
  display: flex;
  align-items: center;
  pointer-events: none;
}

.autocomplete__spinner-svg {
  width: 16px;
  height: 16px;
  animation: autocomplete-spin 0.8s linear infinite;
}

.autocomplete__spinner-circle {
  stroke: var(--halsa-green);
  stroke-dasharray: 40;
  stroke-dashoffset: 15;
  stroke-linecap: round;
}

@keyframes autocomplete-spin {
  to {
    transform: rotate(360deg);
  }
}

.autocomplete__clear {
  position: absolute;
  right: 0.5rem;
  background: none;
  border: none;
  box-shadow: none;
  padding: 0 0.2rem;
  height: auto;
  font-size: 1.1rem;
  line-height: 1;
  color: var(--text-grey);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
}

.autocomplete__clear:hover {
  color: var(--text-black);
}

.autocomplete__error {
  font-size: 0.8rem;
  color: var(--halsa-red);
  margin: 0;
}

.autocomplete__listbox {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 100;
  background: #fff;
  border: 1px solid var(--gray-200);
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  margin-top: 2px;
  padding: 4px 0;
  list-style: none;
  max-height: 240px;
  overflow-y: auto;
}

.autocomplete__option {
  padding: 0.5rem 0.75rem;
  font-family: 'Roboto', sans-serif;
  font-size: 0.95rem;
  color: var(--text-black);
  cursor: pointer;
}

.autocomplete__option--active {
  background: rgba(0, 209, 181, 0.12);
  color: var(--halsa-green-dark);
}

.autocomplete__option:hover {
  background: rgba(0, 209, 181, 0.08);
}
</style>
